'use client';

import gsap from 'gsap';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { CustomImage as Image } from 'components/shared';
import { useRef } from 'react';
import { useWindowSize } from 'hooks/dom/useWindowSize';
// import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
// import { Container } from '..';
import CustomLink from 'components/shared/CustomLink';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { H2Large } from './styleConstants';
import { useTranslations } from 'next-intl';
import styles from './StackCardComponent.module.css';

export const StackCardComponent = ({
  className,
}: {
  className?: string;
}) => {
    const t = useTranslations('powerplugs');
    const gsapContext = useGsapContext();
    const intersectionObserver = useRef<IntersectionObserver | null>(null);
    const container = useRef<HTMLDivElement>(null);
    const { width, height } = useWindowSize();
    const logoAlt = t('powerplugsPage.stack.logoAlt');
    const animationInit = () => {
      return new Promise((resolve, reject) => {
        if (!container.current || !window) return reject(new Error('Error!'));
        gsapContext.add(() => {
          const tl = gsap.timeline({
            defaults: { duration: 2, ease: 'circ.out' },
            repeat: 1,
            yoyo: true,
            scrollTrigger: {
              immediateRender: false,
              trigger: container.current,
              start: 'top top',
              end: () => `top top-=${height * 0.5}`,
              scrub: 0.5,
            },
          });

          if (!tl) return reject(new Error('Error!'));
          tl.set('card-7', { yPercent: 0 });
          tl.to('.card-7', { yPercent: -75 });
        });
        resolve('Success animationInit!');
      });
    };

    const animationMblInit = () => {
      return new Promise((resolve, reject) => {
        if (!container.current || !window) return reject(new Error('Error!'));
        gsapContext.add(() => {
          const tl = gsap.timeline({
            defaults: { duration: 2, ease: 'circ.out' },
            repeat: 1,
            yoyo: true,
            scrollTrigger: {
              immediateRender: false,
              trigger: container.current,
              start: 'top top',
              end: () => `top top-=${height * 0.45}`,
              scrub: 0.5,
            },
          });

          if (!tl) return reject(new Error('Error!'));
          tl.set('card-9', { yPercent: 0 });
          tl.to('.card-9', { yPercent: -75 });
        });
        resolve('Success animationInit!');
      });
    };

    const animationA = () => {
      return new Promise((resolve, reject) => {
        if (!container.current || !window)
          return reject(new Error('Error animationA!'));

        const tlA = gsap.timeline({
          defaults: { duration: 2, ease: 'circ.out' },
          repeat: 1,
          yoyo: true,
          scrollTrigger: {
            immediateRender: false,
            trigger: container.current,
            start: () => `top top-=${height * 0.5}`,
            // end: 'top top-=65%',
            end: () => `top top-=${height * 1}`,
            scrub: 0.5,
          },
        });

        if (!tlA) return reject(new Error('Error animationA!'));

        tlA.set('.card-9', { yPercent: 0 });

        tlA.to('.card-9', { yPercent: -75 });

        resolve('Success animationA!');
      });
    };

    const animationMblA = () => {
      return new Promise((resolve, reject) => {
        if (!container.current || !window)
          return reject(new Error('Error animationA!'));

        const tlA = gsap.timeline({
          defaults: { duration: 2, ease: 'circ.out' },
          repeat: 1,
          yoyo: true,
          scrollTrigger: {
            immediateRender: false,
            trigger: container.current,
            start: () => `top top-=${height * 0.45}`,
            // end: 'top top-=65%',
            end: () => `top top-=${height * 0.9}`,
            scrub: 0.5,
          },
        });

        if (!tlA) return reject(new Error('Error animationA!'));
        tlA.set('.card-11', { yPercent: 0 });

        tlA.to('.card-11', { yPercent: -75 });

        resolve('Success animationA!');
      });
    };

    const animationB = () => {
      return new Promise((resolve, reject) => {
        if (!container.current || !window)
          return reject(new Error('Error animationB!'));
        const tlB = gsap.timeline({
          defaults: { duration: 2, ease: 'circ.out' },
          repeat: 1,
          yoyo: true,
          scrollTrigger: {
            immediateRender: false,
            trigger: container.current,
            start: () => `top top-=${height * 1}`,
            end: () => `top top-=${height * 1.5}`,
            scrub: 0.5,
          },
        });

        if (!tlB) return reject(new Error('Error animationB!'));
        tlB.set('.card-11', { yPercent: 0 });

        tlB.to('.card-11', { yPercent: -75 });

        resolve('Success animationB!');
      });
    };

    const animationMblB = () => {
      return new Promise((resolve, reject) => {
        if (!container.current || !window)
          return reject(new Error('Error animationB!'));
        const tlB = gsap.timeline({
          defaults: { duration: 2, ease: 'circ.out' },
          repeat: 1,
          yoyo: true,
          scrollTrigger: {
            immediateRender: false,
            trigger: container.current,
            start: () => `top top-=${height * 0.9}`,
            end: () => `top top-=${height * 1.35}`,
            scrub: 0.5,
          },
        });

        if (!tlB) return reject(new Error('Error animationB!'));
        tlB.set('.card-13', { yPercent: 0 });

        tlB.to('.card-13', { yPercent: -75 });

        resolve('Success animationB!');
      });
    };

    const animationC = () => {
      return new Promise((resolve, reject) => {
        if (!container.current || !window)
          return reject(new Error('Error animationC!'));
        const tlB = gsap.timeline({
          defaults: { duration: 2, ease: 'circ.out' },
          repeat: 1,
          yoyo: true,
          scrollTrigger: {
            immediateRender: false,
            trigger: container.current,
            start: () => `top top-=${height * 1.5}`,
            end: () => `top top-=${height * 2}`,
            scrub: 0.5,
          },
        });

        if (!tlB) return reject(new Error('Error animationC!'));
        tlB.set('.card-13', { yPercent: 0 });

        tlB.to('.card-13', { yPercent: -75 });

        resolve('Success animationC!');
      });
    };

    const animationFinal = () => {
      return new Promise((resolve, reject) => {
        if (!container.current || !window)
          return reject(new Error('Error AnimationFinal!'));
        const tlB = gsap.timeline({
          defaults: { duration: 2, ease: 'circ.out' },
          repeat: 1,
          yoyo: true,
          scrollTrigger: {
            immediateRender: false,
            trigger: container.current,
            start: () => `top top-=${height * 2}`,
            end: () => `top top-=${height * 2.5}`,
            scrub: 0.5,
          },
        });

        if (!tlB) return reject(new Error('Error AnimationFinal!'));
        tlB.set('.card-15', { yPercent: 0 });

        tlB.to('.card-15', { yPercent: -75 });

        resolve('Success animationFinal!');
      });
    };

    const playAnimations = () => {
      if (!container.current || !window) return;
      if (width < 768) {
        animationMblInit()
          .then(animationMblA)
          .then(animationMblB)
          .catch((error) => {
            console.error('Animation mbl error:', error);
          });
        console.log('playMobileAnimation');
      } else {
        animationInit()
          .then(animationA)
          .then(animationB)
          .then(animationC)
          .then(animationFinal)
          .catch((error) => {
            console.error('Animation error:', error);
          });
      }
    };

    useIsomorphicLayoutEffect(() => {
      if (!width || !container.current) return;

      intersectionObserver.current = new IntersectionObserver(
        (e) => {
          if (e[0].intersectionRatio <= 0) return;
          playAnimations();
        },
        {
          root: null,
          rootMargin: '800px',
          threshold: 0.01,
        },
      );

      intersectionObserver.current?.observe(container.current);

      return () => {
        intersectionObserver.current?.disconnect();
      };
    }, [width]);

    return (
      <div
        className={[styles.root, className].filter(Boolean).join(' ')}
        ref={container}
      >
        <div className="cards">
          <div className="cards-stack-container">
            <div className="card card-1">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-2">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-3">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-4">
              <div className="inner-element">
                <div className="logo-container" id="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-5">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-6">
              <div className="inner-element">
                <div className="logo-container">
                  <Image
                    src="web_v2/powerplugs/svg/timer.svg"
                    width={width > 750 ? 150 : 75}
                    height={width > 750 ? 150 : 75}
                    alt={logoAlt}
                  />
                </div>
              </div>
            </div>
            <div className="card card-7">
              <div className="inner-element">
                <div className="logo-container">
                  <Image
                    src="web_v2/powerplugs/svg/Afib.svg"
                    fill
                    alt={logoAlt}
                  />
                </div>
              </div>
            </div>
            <div className="card card-8">
              <div className="inner-element">
                <div className="logo-container">
                  <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    // fill
                    width={width > 750 ? 150 : 75}
                    height={width > 750 ? 150 : 75}
                    alt={logoAlt}
                  />
                </div>
              </div>
            </div>
            <div className="card card-9">
              <div className="inner-element">
                <div className="logo-container">
                  <Image
                    src="web_v2/powerplugs/svg/Circadian.svg"
                    fill
                    alt={logoAlt}
                  />
                </div>
              </div>
            </div>
            <div className="card card-10">
              <div className="inner-element">
                <div className="logo-container">
                  <Image
                    src="web_v2/powerplugs/svg/weightDisabled.svg"
                    // fill
                    width={width > 750 ? 150 : 75}
                    height={width > 750 ? 150 : 75}
                    alt={logoAlt}
                  />
                </div>
              </div>
            </div>
            <div className="card card-11">
              <div className="inner-element">
                <div className="logo-container">
                  <Image
                    src="web_v2/powerplugs/svg/PeriodTracking.svg"
                    fill
                    alt={logoAlt}
                  />
                </div>
              </div>
            </div>
            <div className="card card-12">
              <div className="inner-element">
                <div className="logo-container">
                  <Image
                    src="web_v2/powerplugs/svg/CircleDisabled.svg"
                    // fill
                    width={width > 750 ? 150 : 75}
                    height={width > 750 ? 150 : 75}
                    alt={logoAlt}
                  />
                </div>
              </div>
            </div>
            <div className="card card-13">
              <div className="inner-element">
                <div className="logo-container">
                  <Image
                    src="web_v2/powerplugs/svg/VitaminD.svg"
                    fill
                    alt={logoAlt}
                  />
                </div>
              </div>
            </div>
            <div className="card card-14">
              <div className="inner-element">
                <div className="logo-container">
                  <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    width={width > 750 ? 150 : 75}
                    height={width > 750 ? 150 : 75}
                    alt={logoAlt}
                  />
                </div>
              </div>
            </div>
            <div className="card card-15">
              <div className="inner-element">
                <div className="logo-container">
                  <Image
                    src="web_v2/powerplugs/svg/Heart.svg"
                    fill
                    alt={logoAlt}
                  />
                </div>
              </div>
            </div>
            <div className="card card-16">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-17">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/Circadian.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-18">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-19">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-20">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-21">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-22">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-23">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-24">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-25">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-26">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-27">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
            <div className="card card-28">
              <div className="inner-element">
                <div className="logo-container">
                  {/* <Image
                    src="web_v2/powerplugs/svg/JetlagDisabled.svg"
                    fill
                    alt={logoAlt}
                  /> */}
                </div>
              </div>
            </div>
          </div>

          <div className="card-content">
            <div className="text-container">
              <H2Large>
                {t('powerplugsPage.stack.titleLine1')} <br />{' '}
                {t('powerplugsPage.stack.titleLine2')}
              </H2Large>
              <p>{t('powerplugsPage.stack.description')}</p>
              <CustomLink href="https://ring.ultrahuman.com/ultrasignal">
                {t('powerplugsPage.stack.getInTouch')}
              </CustomLink>
            </div>
          </div>
        </div>
      </div>
    );
};
